import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Api } from "../../common/service/Api";
import { DataRequestPublicId, OrganizationPublicId } from "../../common/service/server/Types";

export interface DataRequestAttachment {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  status: string;
  uploadedAt: string;
  url: string;
}

export const useDataRequestAttachments = (
  organizationId: OrganizationPublicId,
  requestId: DataRequestPublicId,
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;

  const queryKey = ["dataRequestAttachments", organizationId, requestId];

  const { data: attachments = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => Api.dataRequest.getAttachments(organizationId, requestId),
    enabled: enabled && !!organizationId && !!requestId,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => Api.dataRequest.uploadAttachment(organizationId, requestId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (attachmentId: string) =>
      Api.dataRequest.deleteAttachment(organizationId, requestId, attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const uploadFile = async (file: File): Promise<void> => {
    await uploadMutation.mutateAsync(file);
  };

  const deleteFile = async (attachmentId: string): Promise<void> => {
    await deleteMutation.mutateAsync(attachmentId);
  };

  return {
    attachments: attachments as DataRequestAttachment[],
    isLoading,
    uploadFile,
    deleteFile,
    isUploading: uploadMutation.isLoading,
    isDeleting: deleteMutation.isLoading,
  };
};
